import React from "react";
import { AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Inline error banner shown when one or more data fetches for a page fail.
 *
 * Used in conjunction with `runWithSettled()` below. We deliberately keep the
 * banner non-blocking — successful data still renders behind it — so a stale
 * but partially-loaded page is more useful than a blank screen.
 */
export const ResilientFetchBanner = ({
  failedLabels = [],
  onRetry,
  retrying = false,
  className = "",
  testid = "fetch-error-banner",
}) => {
  if (!failedLabels.length) return null;
  return (
    <div
      data-testid={testid}
      className={`flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50 ${className}`}
    >
      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-amber-900">
          Couldn't refresh: {failedLabels.join(", ")}
        </p>
        <p className="text-[11px] text-amber-700 mt-0.5">
          The rest of the page is still up-to-date. Tap Retry to refresh just the missing parts.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onRetry}
        disabled={retrying}
        data-testid={`${testid}-retry`}
        className="h-8 rounded-full border-amber-300 text-amber-900 hover:bg-amber-100 shrink-0"
      >
        {retrying ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
        <span className="ml-1">Retry</span>
      </Button>
    </div>
  );
};

/**
 * Run a set of labelled async tasks with Promise.allSettled.
 *
 * Returns:
 *   {
 *     results:   { [label]: rawResult | undefined },   // only fulfilled keys present
 *     failures:  [{ label, error }],                   // rejected entries
 *   }
 *
 * Callers can confidently render every successful piece even when one or more
 * fetches fail — no `Promise.all` all-or-nothing semantics.
 *
 * Example:
 *   const { results, failures } = await runWithSettled({
 *     students: () => api.get("/users?role=student"),
 *     batches:  () => api.get("/batches"),
 *   });
 *   if (results.students) setStudents(results.students.data.users);
 *   setFailures(failures);    // -> drives <ResilientFetchBanner>
 */
export const runWithSettled = async (tasksByLabel) => {
  const entries = Object.entries(tasksByLabel);
  const settled = await Promise.allSettled(entries.map(([, fn]) => fn()));

  const results = {};
  const failures = [];
  settled.forEach((res, i) => {
    const [label] = entries[i];
    if (res.status === "fulfilled") {
      results[label] = res.value;
    } else {
      failures.push({ label, error: res.reason });
    }
  });
  return { results, failures };
};

export default ResilientFetchBanner;
